import { useEffect, useState } from 'react'
import { PartyPopper } from 'lucide-react'
import { formatDollars } from '@/lib/format'
import type { Event } from '@/types'

export default function FundraisingProgressBar({ event }: { event: Event }) {
  const raised = event.donation_total_cents ?? 0
  const goal = event.donation_goal_cents ?? 0
  const show = !!event.show_donation_progress && goal > 0
  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0
  const reached = pct >= 100

  // Animate the fill from 0 to its real width on mount so the bar "fills in"
  // when the page loads. Start at 0, then flip to pct after first paint.
  const [fill, setFill] = useState(0)
  useEffect(() => {
    if (!show) return
    const id = requestAnimationFrame(() => setFill(pct))
    return () => cancelAnimationFrame(id)
  }, [show, pct])

  if (!show) return null

  return (
    <div className="group rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/5 to-secondary/10 p-5 shadow-xs ring-1 ring-black/5 transition-shadow hover:shadow-md sm:p-6">
      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <p className="text-2xl font-semibold tabular-nums text-foreground sm:text-3xl">
            {formatDollars(raised)}
          </p>
          <p className="text-sm text-muted-foreground tabular-nums">
            raised of {formatDollars(goal)} goal
          </p>
        </div>
        {reached ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary/20 px-3 py-1 text-sm font-semibold text-foreground">
            <PartyPopper className="size-4 text-primary" />
            Goal reached
          </span>
        ) : (
          <span className="text-3xl font-semibold tabular-nums text-primary sm:text-4xl">{pct}%</span>
        )}
      </div>

      <div
        className="relative h-4 overflow-hidden rounded-full bg-muted ring-1 ring-inset ring-black/5"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${pct}% of fundraising goal reached`}
      >
        <div
          className="relative h-full rounded-full bg-gradient-to-r from-primary to-secondary transition-[width] duration-1000 ease-out motion-reduce:transition-none"
          style={{ width: `${fill}%` }}
        >
          {/* Moving sheen for a lively, "live" feel */}
          <div className="absolute inset-0 overflow-hidden rounded-full">
            <div className="animate-progress-shimmer h-full w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          </div>
        </div>
      </div>
    </div>
  )
}
